import React, { useMemo, useState, useEffect } from "react";
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";
import styled from "styled-components";

const ChartWrapper = styled.div`
  background-color: #f9f9f9;
  width: 100%;
  height: 60vh;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  position: relative;
  box-sizing: border-box;
`;

const FiltersWrapper = styled.div`
  display: flex;
  gap: 20px;
  justify-content: center;
  margin-bottom: 6px;
  align-items: center;
`;

const Select = styled.select`
  padding: 6px 10px;
  font-size: 15px;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const Title = styled.h3`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-weight: bold;
  text-align: center;
  color: #666;
  margin-bottom: 12px;
`;

const InfoIconWrapper = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  &:hover { color: #000; }
  svg { width: 18px; height: 18px; }
`;

// Helpers
const getDayOfYear = (date) => {
  const start = new Date(date.getFullYear(), 0, 0);
  const diff = date - start;
  const oneDay = 1000 * 60 * 60 * 24;
  return Math.floor(diff / oneDay); // 1..366
};

const isLeap = (y) => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;

const DailyUniqueSuppliersChart = ({ data }) => {
  const [selectedYear, setSelectedYear] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);

  // Build available years from DataZakupu
  useEffect(() => {
    const years = Array.from(
      new Set(
        (data || [])
          .map((item) => {
            const d = new Date(item.DataZakupu);
            return isNaN(d) ? null : d.getFullYear();
          })
          .filter(Boolean)
      )
    ).sort((a, b) => b - a);
    setAvailableYears(years);
    if (!selectedYear && years.length) setSelectedYear(years[0]);
  }, [data]);

  const processedData = useMemo(() => {
    if (!selectedYear) return [];

    // prepare maps for current and previous year
    const currSets = {};
    const prevSets = {};

    (data || []).forEach((item) => {
      const d = new Date(item.DataZakupu);
      if (isNaN(d)) return;

      const y = d.getFullYear();
      const key = item.NrDostawcy || item.NazwaDostawcy || "(brak)";

      const day = getDayOfYear(d); // 1..366

      if (y === selectedYear) {
        if (!currSets[day]) currSets[day] = new Set();
        currSets[day].add(key);
      } else if (y === selectedYear - 1) {
        if (!prevSets[day]) prevSets[day] = new Set();
        prevSets[day].add(key);
      }
    });

    const daysInCurr = isLeap(selectedYear) ? 366 : 365;
    // align to current year's length; extra day (Feb 29) in prev year is fine (will be ignored if curr is 365)
    const length = daysInCurr;

    const out = Array.from({ length }, (_, i) => {
      const day = i + 1;
      const suppliersCurr = currSets[day]?.size ?? 0;
      // Use null (not 0) when there is no value for previous year to avoid implying zero if no data
      const suppliersPrev =
        prevSets[day] ? prevSets[day].size : null;

      return {
        day,
        suppliersCurr,
        suppliersPrev,
      };
    });

    return out;
  }, [data, selectedYear]);

  return (
    <ChartWrapper>
      <InfoIconWrapper data-tooltip-id="daily-unique-suppliers-tooltip">
        <Info />
      </InfoIconWrapper>
      <InfoTooltip
        id="daily-unique-suppliers-tooltip"
        place="left"
        content={
          <>
            <strong>Unikalni dostawcy dziennie</strong><br />
            Porównanie {selectedYear} (fiolet) vs {selectedYear ? selectedYear - 1 : "—"} (czerwony).
          </>
        }
      />

      <FiltersWrapper>
        <label>
          Rok:&nbsp;
          <Select
            value={selectedYear || ""}
            onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </Select>
        </label>
      </FiltersWrapper>

      <Title>
        Dzienna liczba unikalnych dostawców — {selectedYear} vs {selectedYear ? selectedYear - 1 : "—"}
      </Title>

      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={processedData}>
          <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" />
          <XAxis
            dataKey="day"
            tick={{ fontSize: 10 }}
            tickLine={false}
            axisLine={{ stroke: "#ddd" }}
          />
          <YAxis
            allowDecimals={false}
            tickLine={false}
            axisLine={{ stroke: "#ddd" }}
          />
          <Tooltip
            formatter={(value, name) => [
              Number(value).toLocaleString("pl-PL"),
              name,
            ]}
            labelFormatter={(d) => `Dzień roku: ${d}`}
          />
          <Legend />

          {/* Current year (purple) */}
          <Line
            type="monotone"
            dataKey="suppliersCurr"
            stroke="#9848c7"
            name={`Dostawcy (${selectedYear})`}
            dot={false}
            strokeWidth={2}
            isAnimationActive={false}
          />

          {/* Previous year (red). Use connectNulls=false to break where no data */}
          <Line
            type="monotone"
            dataKey="suppliersPrev"
            stroke="#dc3545"
            name={`Dostawcy (${selectedYear ? selectedYear - 1 : ""})`}
            dot={false}
            strokeWidth={2}
            isAnimationActive={false}
            connectNulls={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </ChartWrapper>
  );
};

export default DailyUniqueSuppliersChart;
